import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { toast } from 'sonner';
import { ApiError } from '../../../lib/api-client.js';
import { Button } from '../../../shared/components/ui/Button.js';
import { Input } from '../../../shared/components/ui/Input.js';
import { Modal } from '../../../shared/components/ui/Modal.js';
import { serversApi, type Server } from '../api.js';

interface Props {
  open: boolean;
  onClose: () => void;
  /** Se passado, edita; senão cria. */
  server?: Server;
}

export function ServerFormModal({ open, onClose, server }: Props) {
  const isEdit = !!server;
  const [name, setName] = useState(server?.name ?? '');
  const [url, setUrl] = useState(server?.url ?? '');
  const [keyLabel, setKeyLabel] = useState(server?.key_label ?? '');
  const [key, setKey] = useState('');
  const qc = useQueryClient();

  const mut = useMutation({
    mutationFn: async () => {
      if (isEdit && server) {
        const body: Record<string, unknown> = { name, url, key_label: keyLabel };
        if (key) body.key = key;
        return serversApi.patch(server.id, body);
      }
      return serversApi.create({ name, url, key, key_label: keyLabel });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['servers'] });
      toast.success(isEdit ? 'Servidor atualizado' : 'Servidor adicionado');
      onClose();
    },
    onError: (err) => {
      if (err instanceof ApiError) toast.error(err.problem.title, { description: err.problem.detail });
    },
  });

  return (
    <Modal open={open} onClose={onClose} title={isEdit ? 'Editar Servidor' : 'Adicionar Servidor'} width={480}>
      <form
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          mut.mutate();
        }}
        className="flex flex-col gap-4"
      >
        <Input
          label="Nome"
          required
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="ex: FARMA SERV 01"
          autoFocus
        />
        <Input
          label="URL"
          required
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="https://novo.chatplusbr.com"
          hint="Lembre-se de usar https e não http"
        />
        <Input
          label="Label da Key"
          required
          value={keyLabel}
          onChange={(e) => setKeyLabel(e.target.value)}
          placeholder="ex: super-prod"
        />
        <Input
          label={isEdit ? 'API Key (deixe vazio pra manter)' : 'API Key'}
          required={!isEdit}
          value={key}
          onChange={(e) => setKey(e.target.value)}
          placeholder="cpk_..."
          hint="Gere no servidor com `pnpm --filter @chatplus/db platform:key <label>`"
          type="password"
        />
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="submit" loading={mut.isPending}>
            {isEdit ? 'Salvar' : 'Adicionar'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
